import iconComponents from '@/assets/icons/iconComponents'
import clsx from 'clsx'
import {
  AllHTMLAttributes,
  KeyboardEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react'

interface IChips extends AllHTMLAttributes<HTMLDivElement> {
  className?: string
  values?: string[]
  setSelectedValues?: (values: string[]) => void
}

const InputWithChips = (props: IChips) => {
  const [elements, setElements] = useState<string[]>([])
  const { className, values, setSelectedValues = () => {} } = props

  const inputRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setElements(values || [])
  }, [values])

  useEffect(() => {
    setSelectedValues?.(elements)
  }, [elements, setSelectedValues])

  const keyHandler: KeyboardEventHandler<HTMLDivElement> = (e) => {
    const { innerText } = e.currentTarget
    if (!['Enter', 'Tab', 'Backspace', 'Escape'].includes(e.key)) return
    const cleaned = innerText.trim()

    if (['Enter', 'Tab'].includes(e.key) && cleaned) {
      setElements((prev) => {
        if (prev.includes(cleaned)) return prev
        return [...prev, cleaned]
      })
      e.currentTarget.innerText = ''
      e.preventDefault()
    } else if (e.key === 'Backspace' && elements.length && !cleaned) {
      setElements((prev) => prev.slice(0, prev.length - 1))
    } else if (e.key === 'Escape') {
      e.currentTarget.innerText = ''
    }
  }

  const handleDelete = (item: string) => () => {
    setElements((prev) => prev.filter((el) => el !== item))
  }

  return (
    <div
      tabIndex={0}
      role='group'
      aria-label='Input with chips'
      aria-live='polite'
      className={clsx(
        'px-2 py-1.5 outline-none flex gap-2 justify-start items-start content-start max-w-full flex-wrap',
        className || ''
      )}
    >
      {elements.map((el) => (
        <button
          onClick={handleDelete(el)}
          key={el}
          className='!bg-jaa-teal-20 whitespace-nowrap flex justify-center h-[45px] p-2 gap-3 rounded items-center capitalize'
          aria-label={`Remove ${el}`}
        >
          <span className='text-jaa-shades-gray-4 md'>{el}</span>
          <iconComponents.util.CancelIcon className='w-3 h-3 stroke-jaa-dark-teal-80' />
        </button>
      ))}
      <div
        ref={inputRef}
        role='textbox'
        aria-multiline='false'
        aria-label='Add a tag and press Enter'
        contentEditable
        suppressContentEditableWarning
        onKeyDown={keyHandler}
        className='flex-1 outline-none caret-jaa-dark-teal-80 h-[45px] py-2 whitespace-nowrap'
      ></div>
    </div>
  )
}

export default InputWithChips
